import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"

import type { NodeSelection } from "@tiptap/pm/state"
import type { EditorView } from "@tiptap/pm/view"

interface Range {
	from: number
	to: number
}

export const DRAG_SOURCE_CLASS = "pl-drag-source"

// A node view marks the element its drag ghost is built from.
export const DRAG_PREVIEW_ATTRIBUTE = "data-drag-preview"

export const DRAG_PREVIEW_OPACITY = "0.6"

export const dragSourcePluginKey = new PluginKey<DecorationSet>("dragSource")

// prosemirror-view types `dragging` as slice + move only; the node it picked
// up is there at runtime and kept mapped through doc changes.
const draggedNode = (view: EditorView) =>
	(view.dragging as { node?: NodeSelection } | null)?.node

const setSource = (view: EditorView, range: Range | null) => {
	if (view.isDestroyed) return
	view.dispatch(view.state.tr.setMeta(dragSourcePluginKey, range))
}

const clamp = (value: number, max: number) => Math.min(Math.max(value, 0), max)

// The browser's own ghost is nearly opaque and hides the drop line and text
// under the pointer, so the ghost is taken from a see-through copy instead.
const setTranslucentDragImage = (event: DragEvent) => {
	const source = event.target
	if (!(source instanceof HTMLElement) || !event.dataTransfer) return
	const selector = `[${DRAG_PREVIEW_ATTRIBUTE}]`
	const preview = source.matches(selector)
		? source
		: source.querySelector<HTMLElement>(selector)
	if (!preview) return

	const rect = preview.getBoundingClientRect()
	const ghost = document.createElement("div")
	// Laid over the original, so a frame painted before removal looks unchanged.
	Object.assign(ghost.style, {
		position: "fixed",
		left: `${rect.left}px`,
		top: `${rect.top}px`,
		width: `${rect.width}px`,
		height: `${rect.height}px`,
		pointerEvents: "none",
		zIndex: "2147483647",
	})
	const copy = preview.cloneNode(true) as HTMLElement
	copy.removeAttribute(DRAG_PREVIEW_ATTRIBUTE)
	Object.assign(copy.style, {
		display: "block",
		width: "100%",
		height: "100%",
		margin: "0",
		opacity: DRAG_PREVIEW_OPACITY,
	})
	ghost.appendChild(copy)
	document.body.appendChild(ghost)

	event.dataTransfer.setDragImage(
		ghost,
		clamp(event.clientX - rect.left, rect.width),
		clamp(event.clientY - rect.top, rect.height),
	)
	requestAnimationFrame(() => ghost.remove())
}

// Fades the block being dragged, so the reader sees what is about to move.
export const DragSourceExtension = Extension.create({
	name: "dragSource",

	addProseMirrorPlugins() {
		return [
			new Plugin<DecorationSet>({
				key: dragSourcePluginKey,
				state: {
					init: () => DecorationSet.empty,
					apply(tr, set) {
						const range = tr.getMeta(dragSourcePluginKey) as
							| Range
							| null
							| undefined
						if (range === null || tr.getMeta("uiEvent") === "drop") {
							return DecorationSet.empty
						}
						if (range) {
							return DecorationSet.create(tr.doc, [
								Decoration.node(range.from, range.to, {
									class: DRAG_SOURCE_CLASS,
								}),
							])
						}
						return set.map(tr.mapping, tr.doc)
					},
				},
				props: {
					decorations: (state) => dragSourcePluginKey.getState(state),
					handleDOMEvents: {
						dragstart: (view, event) => {
							setTranslucentDragImage(event)
							// Chrome cancels a drag whose source element changes inside
							// dragstart, and the dragged node is only known after it.
							setTimeout(() => {
								const node = draggedNode(view)
								if (node) setSource(view, { from: node.from, to: node.to })
							})
							return false
						},
						dragend: (view) => {
							if (dragSourcePluginKey.getState(view.state)?.find().length) {
								setSource(view, null)
							}
							return false
						},
					},
				},
			}),
		]
	},
})
